export const STARTING_SPINS = 8;
export const MAX_TOTAL_SPINS = 11;
export const TARGET_SCORE = 150;
export const MAX_HP = 5;
const STARTING_NUDGES = 2;

export const SYMBOLS = {
  0: { glyph: '0', name: 'Zero', value: 0, kind: 'digit' },
  1: { glyph: '1', name: 'One', value: 1, kind: 'digit' },
  2: { glyph: '2', name: 'Two', value: 2, kind: 'digit' },
  3: { glyph: '3', name: 'Three', value: 3, kind: 'digit' },
  4: { glyph: '4', name: 'Four', value: 4, kind: 'digit' },
  5: { glyph: '5', name: 'Five', value: 5, kind: 'digit' },
  6: { glyph: '6', name: 'Six', value: 6, kind: 'digit' },
  7: { glyph: '7', name: 'Seven', value: 7, kind: 'lucky' },
  8: { glyph: '8', name: 'Eight', value: 8, kind: 'digit' },
  9: { glyph: '9', name: 'Nine', value: 9, kind: 'digit' },
  crown: { glyph: '♛', name: 'Crown', value: 10, kind: 'crown' },
  eye: { glyph: '◉', name: 'Eye', value: 5, kind: 'eye' },
  skull: { glyph: '☠', name: 'Skull', value: 0, kind: 'skull' },
  wild: { glyph: '✦', name: 'Wild', value: 7, kind: 'lucky' },
};

export const REEL_STRIP = Object.keys(SYMBOLS);

export const CONTRACTS = {
  twinEngine: {
    name: 'Twin Engine',
    boon: 'Any matching pair pays +18.',
    curse: 'Three different symbols pay −2.',
    accent: 'violet',
  },
  sevenDebt: {
    name: 'Seven Debt',
    boon: 'Every Seven or Wild pays +12.',
    curse: 'A spin without either pays −3.',
    accent: 'gold',
  },
  crownPact: {
    name: 'Crown Pact',
    boon: 'Every Crown pays +15.',
    curse: 'A spin without a Crown pays −2.',
    accent: 'royal',
  },
  eyeOfTheHouse: {
    name: 'Eye of the House',
    boon: 'Every Eye pays +9.',
    curse: 'Two or more Skulls cost 1 extra HP.',
    accent: 'cyan',
  },
  highVoltage: {
    name: 'High Voltage',
    boon: 'Strictly ascending values pay +15.',
    curse: 'Strictly descending values pay −6.',
    accent: 'electric',
  },
  oddPact: {
    name: 'Odd Pact',
    boon: 'Each odd valued symbol pays +5.',
    curse: 'No odd values cost 1 extra HP.',
    accent: 'red',
  },
};

export const createGame = (progress = {}) => ({
  phase: 'intro',
  spinNumber: 1,
  totalSpins: STARTING_SPINS,
  score: 0,
  hp: MAX_HP,
  nudges: STARTING_NUDGES,
  contracts: [],
  reels: null,
  lastNudgedIndex: null,
  spinVersion: 0,
  result: null,
  history: [],
  bestScore: progress.bestScore || 0,
  victories: progress.victories || 0,
});

export const startRun = (game) => ({ ...createGame(game), phase: 'ready' });

export const getContractOptions = (game) => {
  const available = Object.keys(CONTRACTS).filter(
    (id) => !game.contracts.includes(id)
  );
  if (game.spinNumber === 3) return available.slice(0, 3);
  if (game.spinNumber === 5) return available.slice(-3);
  if (game.spinNumber === 7) return available.slice(0, 3);
  return [];
};

export const takeContract = (game, id) => {
  if (game.phase !== 'contract' || !getContractOptions(game).includes(id))
    return game;
  return {
    ...game,
    phase: 'ready',
    contracts: [...game.contracts, id],
    nudges: Math.min(3, game.nudges + 1),
  };
};

const validSymbol = (symbol) => typeof symbol === 'string' && !!SYMBOLS[symbol];

export const spin = (game, reels) => {
  if (
    game.phase !== 'ready' ||
    !Array.isArray(reels) ||
    reels.length !== 3 ||
    !reels.every(validSymbol)
  )
    return game;
  return {
    ...game,
    phase: 'spun',
    reels: [...reels],
    lastNudgedIndex: null,
    spinVersion: game.spinVersion + 1,
  };
};

export const nudge = (game, index, symbol) => {
  if (
    game.phase !== 'spun' ||
    game.nudges < 1 ||
    ![0, 1, 2].includes(index) ||
    !validSymbol(symbol)
  )
    return game;
  const reels = [...game.reels];
  reels[index] = symbol;
  return {
    ...game,
    reels,
    nudges: game.nudges - 1,
    lastNudgedIndex: index,
    spinVersion: game.spinVersion + 1,
  };
};

const countWhere = (reels, predicate) =>
  reels.filter((id) => predicate(SYMBOLS[id], id)).length;

export const getSpinPreview = (game) => {
  if (!game.reels || game.reels.length !== 3 || !game.reels.every(validSymbol))
    return null;

  const effects = [];
  const add = (label, amount, kind = 'boon') =>
    effects.push({ label, amount, kind });
  const values = game.reels.map((id) => SYMBOLS[id].value);
  const uniqueCount = new Set(game.reels).size;
  const isTriple = uniqueCount === 1;
  const hasPair = uniqueCount === 2;
  const luckyCount = countWhere(
    game.reels,
    (symbol) => symbol.kind === 'lucky'
  );
  const crownCount = countWhere(
    game.reels,
    (symbol) => symbol.kind === 'crown'
  );
  const eyeCount = countWhere(game.reels, (symbol) => symbol.kind === 'eye');
  const skullCount = countWhere(
    game.reels,
    (symbol) => symbol.kind === 'skull'
  );
  const oddCount = values.filter((value) => value % 2 === 1).length;
  const ascending = values[0] < values[1] && values[1] < values[2];
  const descending = values[0] > values[1] && values[1] > values[2];
  let event = 'normal';
  let bonusSpins = 0;

  if (isTriple && game.reels[0] === 'skull') {
    add("Death's jackpot", 0, 'curse');
    event = 'doom';
  } else if (isTriple) {
    add(
      game.reels[0] === '7' ? 'TRIPLE SEVEN JACKPOT' : 'THREE OF A KIND',
      game.reels[0] === '7' ? 80 : 40,
      'jackpot'
    );
    event = 'jackpot';
    bonusSpins = 1;
  } else if (hasPair) {
    add('Matching pair', 12);
  }
  if (luckyCount >= 2 && !isTriple) {
    add('Seven near miss', 15, 'nearMiss');
    event = 'nearMiss';
  }
  if (crownCount + countWhere(game.reels, (_, id) => id === 'wild') >= 2) {
    add('Crown bonus round', 10, 'bonus');
    bonusSpins = Math.max(bonusSpins, 1);
    if (event === 'normal') event = 'bonus';
  }
  if (skullCount >= 2) {
    add('Skull line', -5, 'curse');
    if (event === 'normal') event = 'doom';
  }

  if (game.contracts.includes('twinEngine'))
    add(
      'Twin Engine',
      uniqueCount < 3 ? 18 : -2,
      uniqueCount < 3 ? 'boon' : 'curse'
    );
  if (game.contracts.includes('sevenDebt'))
    add(
      'Seven Debt',
      luckyCount ? luckyCount * 12 : -3,
      luckyCount ? 'boon' : 'curse'
    );
  if (game.contracts.includes('crownPact'))
    add(
      'Crown Pact',
      crownCount ? crownCount * 15 : -2,
      crownCount ? 'boon' : 'curse'
    );
  if (game.contracts.includes('eyeOfTheHouse') && eyeCount)
    add('Eye of the House', eyeCount * 9);
  if (game.contracts.includes('highVoltage')) {
    if (ascending) add('High Voltage', 15);
    if (descending) add('High Voltage curse', -6, 'curse');
  }
  if (game.contracts.includes('oddPact') && oddCount)
    add('Odd Pact', oddCount * 5);

  const base = values.reduce((total, value) => total + value, 0);
  const payout = Math.max(
    0,
    base + effects.reduce((total, effect) => total + effect.amount, 0)
  );
  const lowPayoutDamage = payout < 10 ? 1 : 0;
  const skullDamage = skullCount >= 2 ? (skullCount === 3 ? 2 : 1) : 0;
  const contractDamage =
    (game.contracts.includes('eyeOfTheHouse') && skullCount >= 2 ? 1 : 0) +
    (game.contracts.includes('oddPact') && oddCount === 0 ? 1 : 0);

  return {
    base,
    effects,
    payout,
    hpLoss: lowPayoutDamage + skullDamage + contractDamage,
    event,
    bonusSpins,
  };
};

export const lockSpin = (game) => {
  if (game.phase !== 'spun') return game;
  const preview = getSpinPreview(game);
  if (!preview) return game;
  const hp = Math.max(0, game.hp - preview.hpLoss);
  const score = game.score + preview.payout;
  const totalSpins = Math.min(
    MAX_TOTAL_SPINS,
    game.totalSpins + preview.bonusSpins
  );
  const won = hp > 0 && score >= TARGET_SCORE;
  const lost = hp === 0 || game.spinNumber >= totalSpins;
  const result = {
    ...preview,
    reels: [...game.reels],
    hpAfter: hp,
    scoreAfter: score,
  };

  return {
    ...game,
    phase: won ? 'won' : lost ? 'lost' : 'result',
    hp,
    score,
    totalSpins,
    result,
    history: [result, ...game.history].slice(0, MAX_TOTAL_SPINS),
    bestScore: won || lost ? Math.max(game.bestScore, score) : game.bestScore,
    victories: game.victories + (won ? 1 : 0),
  };
};

export const continueRun = (game) => {
  if (game.phase !== 'result') return game;
  return {
    ...game,
    spinNumber: game.spinNumber + 1,
    phase: [2, 4, 6].includes(game.spinNumber) ? 'contract' : 'ready',
    reels: null,
    lastNudgedIndex: null,
    result: null,
  };
};
